import { useContext, useMemo } from 'react';
import { generatePath } from 'react-router-dom';
import { DEAL_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { useMipsByDealId } from '@dealstack/frontend/app/pages/Project/MIP/hooks';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { QuickSwitchConfigItem } from '@dealstack/frontend/shared/components/QuickSwitch/QuickSwitchMenu.types';
import { QuickSwitchContext } from '@dealstack/frontend/shared/components/QuickSwitch/useQuickSwitch';
import { GENERIC_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds.ts';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { useDealEventLogUnread } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventLogUnread.ts';
import { formatUnreadBadge } from '@dealstack/frontend/app/pages/Project/DealEventLog/dealEventLog.utils.ts';

const EVENT_LOG_ITEM_ID = 'event-log';

const DEFAULT_CONFIG: QuickSwitchConfigItem[] = [
	{
		id: 'cap-table',
		icon: 'table-v3',
		label: 'quick_switch_menu.cap_table',
		route: DEAL_ROUTES.CAP_TABLE,
		dataTestId: GENERIC_ELEMENT_ID.QUICK_SWITCHER_MENU_CAP_TABLE,
		matchOnlyExactRoutes: true,
	},
	{
		id: 'group-structure-chart',
		icon: 'branch',
		label: 'quick_switch_menu.group_structure_chart',
		route: DEAL_ROUTES.CAP_CHART,
		dataTestId: GENERIC_ELEMENT_ID.QUICK_SWITCHER_MENU_GSC,
	},
	{
		id: 'deal-directory',
		icon: 'open-book',
		label: 'quick_switch_menu.deal_directory',
		route: generatePath(DEAL_ROUTES.PEOPLE, { personId: '' }),
		dataTestId: GENERIC_ELEMENT_ID.QUICK_SWITCHER_MENU_DEAL_DIRECTORY,
	},
	{
		id: 'document-library',
		icon: 'document-v3-rounded',
		label: 'quick_switch_menu.document_library',
		route: DEAL_ROUTES.DOCUMENT_LIBRARY,
	},
	{
		id: 'valuation',
		icon: 'percentage',
		label: 'quick_switch_menu.valuation',
		route: DEAL_ROUTES.WATERFALL,
		dataTestId: GENERIC_ELEMENT_ID.QUICK_SWITCHER_MENU_VALUATION,
	},
	{
		id: EVENT_LOG_ITEM_ID,
		icon: 'clock',
		label: 'quick_switch_menu.event_log',
		route: DEAL_ROUTES.EVENT_LOG,
		dataTestId: GENERIC_ELEMENT_ID.QUICK_SWITCHER_MENU_EVENT_LOG,
	},
	{
		id: 'deal-settings',
		icon: 'settings',
		label: 'quick_switch_menu.deal_settings',
		route: DEAL_ROUTES.SETTINGS,
	},
];

/**
 * Hook that provides the QuickSwitch configuration with default items and potential MIP items.
 * Respects context configuration if available, otherwise falls back to default + MIP items.
 *
 * @param overrideConfig - Optional configuration to override the default
 * @returns QuickSwitch configuration items
 */
export function useQuickSwitchConfig(
	overrideConfig?: QuickSwitchConfigItem[]
): QuickSwitchConfigItem[] {
	const { dealId } = useRequiredParams('dealId');
	const context = useContext(QuickSwitchContext);

	const getMipsRequest = useMipsByDealId(dealId);

	const { hasPermission: hasViewerPermission } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: defaultToEmptyString(dealId),
		permissionRole: PermissionRole.VIEWER,
	});

	const { value: eventLogEnabled } = useDealstackFeatureFlag(FEATURE_FLAG.DEAL_EVENT_LOG);
	const { data: eventLogUnread } = useDealEventLogUnread(
		defaultToEmptyString(dealId),
		eventLogEnabled && hasViewerPermission
	);
	const eventLogBadge = formatUnreadBadge(eventLogUnread);

	const mipItems = useMemo(
		() =>
			getMipsRequest.data
				?.filter((mip) => !!mip.entityId)
				.map(({ id, programmeName }) => ({
					id,
					label: programmeName || 'Draft MIP',
					route: DEAL_ROUTES.MIP.replace(':mipId', id),
				})) ?? [],
		[getMipsRequest.data]
	);

	const mipParentItem = useMemo(() => {
		if (mipItems.length === 0) {
			return null;
		}

		return {
			id: 'investment-programme',
			icon: 'plant-grown' as const,
			label: 'quick_switch_menu.investment_programmes',
			items: mipItems,
		};
	}, [mipItems]);

	const config = useMemo(() => {
		if (context?.config) {
			return context.config;
		}

		if (overrideConfig) {
			return overrideConfig;
		}

		const baseConfig: QuickSwitchConfigItem[] = hasViewerPermission
			? DEFAULT_CONFIG.filter((item) => eventLogEnabled || item.id !== EVENT_LOG_ITEM_ID).map(
					(item) =>
						item.id === EVENT_LOG_ITEM_ID ? { ...item, badge: eventLogBadge } : item
				)
			: [];

		if (mipParentItem) {
			baseConfig.push(mipParentItem);
		}

		return baseConfig;
	}, [
		context?.config,
		overrideConfig,
		mipParentItem,
		eventLogEnabled,
		eventLogBadge,
		hasViewerPermission,
	]);

	return config;
}
